import { BadgeCard, BadgeCardWidth } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { CSSProperties, FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMousePosition } from '../../../hooks/useMousePosition';
import { getBadgeCardColors } from '../../../utils/badgeCardColors';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { PickedCard } from '../types';

export interface CardPreviewProps {
  pickedCard: PickedCard;
  preventEscape: boolean;
}

export const CardPreview: FC<CardPreviewProps> = observer(({ pickedCard, preventEscape }) => {
  const card = pickedCard.card;

  const { x, y } = useMousePosition({ defaultX: pickedCard.position.x, defaultY: pickedCard.position.y });
  const store = useTeachingBlockStore();

  const count = store.freeCards.filter(
    (c) => c.lesson?.uuid === card?.lesson?.uuid && c.duration === card.duration,
  ).length;

  const offsetX = -10;
  const offsetY = -10;

  // Darstellung „Fachfarbe für Kärtchen" – die Karte in der Hand zeigt dieselbe Farbe wie im Stapel.
  const badgeColors = getBadgeCardColors({
    colorBySubject: store.cardColorBySubject,
    subjectColor: card.subject?.timetableConfig?.color ?? null,
    rows: card.badgeCardRows ?? [],
    teacherTextColor: card.badgeCardTextColor,
  });

  const style: CSSProperties = {
    position: 'fixed',
    zIndex: 10,
    left: x - offsetX,
    top: y - offsetY,
    opacity: 0.75,
    ...(badgeColors.borderColor ? { outline: `1px solid ${badgeColors.borderColor}` } : {}),
  };

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!preventEscape && event.key === 'Escape') {
        store.onEscape(card);
      }
    }

    function handleClick(event: MouseEvent) {
      // Zweiter Klick eines Doppelklicks (detail >= 2): nicht ablegen/verwerfen –
      // der Doppelklick auf eine Karte soll Auto-Fit auslösen, nicht die aufgenommene
      // Karte wieder fallen lassen.
      if (event.detail >= 2) return;

      const element = event.target as HTMLElement;
      // Ablage ist der gesamte Auswahl-Bereich (`[data-card-stack]` an der picker-panel),
      // nicht nur die Kartenliste (`.drop-and-delete`) – analog zum TimetableGrid.
      const inCardStack = element.classList.contains('drop-and-delete') || !!element.closest('[data-card-stack]');
      if (inCardStack) {
        // Raum-Kontext: Zurücklegen entfernt nur den gepickten Raum (Zeit-Platzierung bleibt);
        // erst wenn kein Raum mehr zugewiesen ist, erscheint die Karte wieder im Picker.
        if (store.gridContext === 'rooms') {
          store.removePickedRoomToStack();
        } else if (card.startDate) {
          store.discardCard(card);
        } else {
          store.pickCard(null);
        }
      } else if (!element.classList.contains('droppable')) {
        store.pickCard(null);
      } else if (store.gridContext !== 'rooms' && (pickedCard.counter ?? 0) < 1) {
        // Raum-Kontext: das Ablegen auf eine Zelle steuert GridWeek (assignRoomToPickedCard);
        // hier NICHT zusätzlich den Pick beenden – sonst ginge der Kopiermodus verloren.
        store.pickCard(null);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [card, store.discardCard, store.pickCard, pickedCard.counter, preventEscape, store]);

  return createPortal(
    <BadgeCard
      label={card.subject?.shortName ?? ''}
      count={count}
      rows={badgeColors.rows}
      width={card.duration as BadgeCardWidth}
      showHover={false}
      showGap={!!badgeColors.rows.length}
      showRounded={false}
      style={style}
      color={badgeColors.color}
      backgroundColor={badgeColors.backgroundColor}
    />,
    document.fullscreenElement ? (document.querySelector('#teaching-block-grid') ?? document.body) : document.body,
  );
});
